import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Popover,
} from "@mui/material";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import html2pdf from "html2pdf.js";
import getWebsiteAnalytics from "../utils/pdfAnalytics";
import UserReview from "./ReportComponents/UserReview";
import SessionCard from "./ReportComponents/SessionCard";
import TopPages from "./ReportComponents/TopPages";
import BrowserData from "./ReportComponents/BrowserData";
import BySource from "./ReportComponents/BySource";
import Insights from "./ReportComponents/Insights";
import CountrySessions from "./ReportComponents/CountrySessions";
import TopEntryExit from "./ReportComponents/TopEntryExit";
import { createGlobalStyle } from "styled-components";

// PDF page dimensions in mm (A4)
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const PX_PER_MM = 3.78; // Approximate conversion from mm to pixels

// Use the logo from the public folder
const LOGO_PATH = "/images/logo.png";

const PDFDesigner = ({ csvData, headers }) => {
  const [documentTitle, setDocumentTitle] = useState("Website Report");
  const [companyName, setCompanyName] = useState("ByteCrafts");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [error, setError] = useState("");
  const [sessionData, setSessionData] = useState({
    totalSessions: 0,
    botSessions: 0,
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [formattedDates, setFormattedDates] = useState({
    startDate: "",
    endDate: "",
  });

  const pageRef = useRef(null);

  // Theme state for component backgrounds
  const [theme, setTheme] = useState({
    headerBg: "#F5F5F5",
    sessionCardBg: "#353535",
    userReviewBg: "#191923",
    topEntryExitBg: "#35564B",
    topPagesBg: "#35564B",
    browserDataBg: "#4D625D",
    bySourceBg: "#A53860",
    insightsBg: "#EDDEA4",
    countrySessionsBg: "#FFFFFF",
    footerBg: "#f8f8f8",
  });

  // State for theme editor popover
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Handler for opening theme editor
  const handleThemeClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handler for closing theme editor
  const handleThemeClose = () => {
    setAnchorEl(null);
  };

  // Handler for updating a theme color
  const handleColorChange = (key, value) => {
    setTheme((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Extract session data from CSV
  useEffect(() => {
    if (csvData && csvData.length > 0) {
      let totalSessions = 0;
      let botSessions = 0;

      csvData.forEach((row) => {
        if (
          row.Sessions === "Total sessions" &&
          row.hasOwnProperty("Total sessions")
        ) {
          totalSessions = parseInt(row["Total sessions"]) || 0;
        } else if (
          row.Sessions === "Bot sessions" &&
          row.hasOwnProperty("Total sessions")
        ) {
          botSessions = parseInt(row["Total sessions"]) || 0;
        } else if (row.Type === "Total sessions" && row.Value) {
          totalSessions = parseInt(row.Value) || 0;
        } else if (row.Type === "Bot sessions" && row.Value) {
          botSessions = parseInt(row.Value) || 0;
        }
      });

      setSessionData({
        totalSessions: totalSessions || 25,
        botSessions: botSessions || 4,
      });
    }
  }, [csvData]);

  useEffect(() => {
    if (!csvData || csvData.length === 0) return;
    const websiteAnalytics = getWebsiteAnalytics(
      csvData,
      "Bapuji Dental College"
    );
    setAnalyticsData(websiteAnalytics);
  }, [csvData]);

  // Format the date range for display
  useEffect(() => {
    if (analyticsData?.dateRange) {
      try {
        const dateRangeFull = analyticsData.dateRange;
        // Expected format: "MM/DD/YYYY HH:MM AM/PM - MM/DD/YYYY HH:MM AM/PM"
        const parts = dateRangeFull?.split(" - ");

        if (parts.length === 2) {
          // Extract just date parts (MM/DD/YYYY) from each side
          const startDateParts = parts[0].split(" ");
          const endDateParts = parts[1].split(" ");

          setFormattedDates({
            startDate: startDateParts[0], // Just the MM/DD/YYYY part
            endDate: endDateParts[0], // Just the MM/DD/YYYY part
          });
        }
      } catch (err) {
        console.error("Error parsing date range:", err);
      }
    }
  }, [analyticsData]);

  // Generate PDF with html2pdf.js - updated for dynamic content size
  const generatePDF = async () => {
    setPdfGenerating(true);
    setError("");

    try {
      if (pageRef && pageRef.current) {
        // Get the actual content dimensions
        const contentElement = pageRef.current;
        const contentHeight = contentElement.scrollHeight;
        const contentWidth = contentElement.scrollWidth;

        // Calculate the PDF dimensions in mm (keeping the width fixed)
        const pdfWidth = PAGE_WIDTH;
        // Convert height from px to mm, add 5% margin
        const pdfHeight = Math.ceil((contentHeight / PX_PER_MM) * 1.05);

        console.log(
          `Content dimensions: ${contentWidth}×${contentHeight}px, PDF dimensions: ${pdfWidth}×${pdfHeight}mm`
        );

        // Improved configuration with better scaling options
        const options = {
          filename: `${documentTitle.replace(/\s+/g, "_")}.pdf`,
          image: { type: "jpeg", quality: 1.0 },
          html2canvas: {
            scale: 2, // Higher scale for better quality
            letterRendering: true,
            useCORS: true,
            scrollY: 0,
            windowHeight: contentHeight,
            logging: true, // Enable logging for debugging
            removeContainer: false, // Keep container in HTML during capture
            allowTaint: true, // Allow tainted canvas if needed for images
            x: 0,
            y: 0,
          },
          jsPDF: {
            unit: "mm",
            format: [pdfWidth, pdfHeight],
            orientation: "portrait",
            compress: true,
            precision: 16, // Increased precision for better positioning
          },
          pagebreak: { mode: "avoid-all" },
        };

        // Generate PDF using the direct save approach
        await html2pdf().from(contentElement).set(options).save();

        setPdfGenerating(false);
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      setError(`Error generating PDF: ${err.message}`);
      setPdfGenerating(false);
    }
  };
  const GlobalPDFStyles = createGlobalStyle`
  @media print {
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    * {
      page-break-inside: avoid;
      break-inside: avoid;
      transform-origin: top left;
    }
    
    img, svg {
      display: block;
      max-width: 100%;
      height: auto;
    }
    
    .MuiTypography-root {
      margin: 0;
      padding: 0;
      transform: translateZ(0);
      position: relative;
    }
  }
`;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 3,
        overflow: "hidden", // Prevent outer container overflow
      }}
    >
      <GlobalPDFStyles />
      {/* Navbar controls at the top */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          backgroundColor: "#f8f8f8",
          padding: "15px",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          flexShrink: 0, // Prevent shrinking
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Website Report
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Document Title"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
            size="small"
            sx={{ width: "200px" }}
          />

          <TextField
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            size="small"
            sx={{ width: "200px" }}
          />

          <IconButton
            color="primary"
            onClick={handleThemeClick}
            title="Customize Theme Colors"
          >
            <ColorLensIcon />
          </IconButton>

          <Button
            variant="contained"
            color="primary"
            onClick={generatePDF}
            disabled={pdfGenerating}
            startIcon={pdfGenerating ? <CircularProgress size={20} /> : null}
          >
            Generate PDF
          </Button>
        </Box>
      </Box>

      {/* Theme Editor Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleThemeClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Customize Theme Colors
          </Typography>

          {/* Replace Grid with div using flexbox layout */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {Object.entries(theme).map(([key, value]) => (
              <div
                key={key}
                style={{
                  width: "calc(50% - 6px)", // 50% width minus half of the gap
                  marginBottom: "8px",
                }}
              >
                <Typography variant="caption" display="block">
                  {key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <input
                    type="color"
                    value={value}
                    onChange={(e) => handleColorChange(key, e.target.value)}
                    style={{ width: "30px", height: "30px" }}
                  />
                  <TextField
                    size="small"
                    value={value}
                    onChange={(e) => handleColorChange(key, e.target.value)}
                    sx={{ width: "100px" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Box>
      </Popover>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, flexShrink: 0 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          mb: 3,
          backgroundColor: "#f5f5f5",
          p: 3,
          borderRadius: 1,
          overflowY: "auto",
          height: "auto", // Changed from 0 to auto
          minHeight: "auto", // Changed from 0 to auto
        }}
      >
        <Paper
          ref={pageRef}
          elevation={3}
          sx={{
            width: `${PAGE_WIDTH * PX_PER_MM}px`,
            backgroundColor: "#ffffff",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
            overflow: "visible",
            padding: "20px",
            height: "auto",
            display: "flex",
            flexDirection: "column",
            position: "relative", // Add explicit position
            transformOrigin: "top left", // Ensure transform origin is consistent
            pageBreakInside: "avoid", // Prevent unexpected page breaks
            breakInside: "avoid",
          }}
        >
          {/* Cards container */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100px",
                backgroundColor: theme.headerBg,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 20px",
                boxSizing: "border-box",
                border: `1px solid ${theme.headerBg}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <img
                  src={LOGO_PATH}
                  alt="ByteCrafts Logo"
                  style={{
                    height: "50px",
                    marginBottom: "5px",
                    objectFit: "contain",
                  }}
                />
              </div>
              <div style={{ textAlign: "center", flex: 1, marginBottom: "20px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: "#333",
                    position: "relative",
                    display: "block",
                    margin: 0,
                    fontSize: "1.5rem",
                    lineHeight: 1.3,
                    marginBottom: "4px",
                  }}
                >
                  Website Analytics Report
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: "#333",
                    position: "relative",
                    display: "block",
                    margin: 0,
                    paddingBottom: "10px",
                    padding: 0,
                    fontSize: "1.5rem",
                    lineHeight: 1.3,
                  }}
                >
                  {companyName}
                </Typography>
              </div>
              <div
                style={{
                  textAlign: "right",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", color: "#333" }}
                >
                  {formattedDates.startDate}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", color: "#333" }}
                >
                  {formattedDates.endDate}
                </Typography>
              </div>
            </div>

            {/* Left Side Cards */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                position: "relative",
              }}
            >
              <SessionCard
                analyticsData={analyticsData}
                themeBg={theme.sessionCardBg}
              />
              <UserReview
                analyticsData={analyticsData}
                themeBg={theme.userReviewBg}
              />
              <TopEntryExit
                analyticsData={analyticsData}
                themeBg={theme.topEntryExitBg}
              />
              <Insights
                analyticsData={analyticsData}
                themeBg={theme.insightsBg}
              />
            </div>

            {/* Right Side Cards */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <TopPages
                analyticsData={analyticsData}
                themeBg={theme.topPagesBg}
              />
              <BrowserData
                analyticsData={analyticsData}
                themeBg={theme.browserDataBg}
              />
              <BySource
                analyticsData={analyticsData}
                themeBg={theme.bySourceBg}
              />
            </div>

            {/* Country Sessions - Full Width */}
            <div
              style={{
                width: "100%",
                marginBottom: "20px",
                backgroundColor: theme.countrySessionsBg,
                borderRadius: "12px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CountrySessions
                analyticsData={analyticsData}
                themeBg={theme.countrySessionsBg}
              />
            </div>

            {/* Footer with contact information in a single line */}
            <div
              style={{
                width: "100%",
                backgroundColor: theme.footerBg,
                borderTop: "1px solid #ddd",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 20px",
                boxSizing: "border-box",
                fontSize: "12px",
                color: "#666",
                marginTop: "20px",
              }}
            >
              <div>Generated by Bytecrafts</div>
              <div style={{ display: "flex", gap: "15px" }}>
                <a
                  href="mailto:support@bytecrafts.in"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    color: "#666",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = "mailto:support@bytecrafts.in";
                  }}
                >
                  <EmailIcon fontSize="small" />
                  <span>support@bytecrafts.in</span>
                </a>
                <a
                  href="https://bytecrafts.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    color: "#666",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open("https://bytecrafts.in", "_blank");
                  }}
                >
                  <LanguageIcon fontSize="small" />
                  <span>bytecrafts.in</span>
                </a>
              </div>
            </div>
          </div>
        </Paper>
      </Box>
    </Box>
  );
};

export default PDFDesigner;
